import { useCallback, useEffect, useId, useRef, useState } from "react";
import isEqual from "lodash/isEqual";
import TemplateParameterEditor from "@/components/documentTemplate/TemplateParameterEditor";
import type { DocumentTemplateParameter } from "@/types/DocumentTemplateParameter";
import { Alert, Box, Tooltip, Typography } from "@mui/material";
import TinyMceEditor from "@/components/tinymce/tinymce";
import TemplateVariables from "@/components/documentTemplate/templateVariables";
import GenericCrudTableModal from "@/components/tables/genericCrudTableModal";
import RadioButtons from "@/components/forms/inputs/radioButtons";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import { IDocumentTemplateData } from "@/types/DocumentTemplateData";
import { IDropDownOption } from "@/types/DropDown";
import { useTranslation } from "react-i18next";
import { api } from "@/helper/api";
import { TextArea } from "@/components/forms/inputs/textArea";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import styles from "@/components/documentTemplate/editor.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import {
  getTemplateSyntaxErrors,
  TemplateSyntaxError,
  useTemplateSyntaxValidation,
} from "@/components/documentTemplate/useTemplateSyntaxValidation";

interface Props {
  id?: string;
  onRemoveEditor?: (id: any) => void;
  onDuplicateEditor?: (data: any) => void;
  onDirtyChange?: (id: string, dirty: boolean) => void;
  // editorCount?: any;
  data?: any;
}

interface IDocumentTemplateOptions extends IDropDownOption {
  type?: string;
}

export function Editor(props: Props) {
  const requestSequence = useRef(0);
  const fallbackId = useId();
  const editorId = props.id ?? fallbackId;
  const [parameterDraftDirty, setParameterDraftDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const savePending = useRef(false);
  const [parameters, setParameters] = useState<DocumentTemplateParameter[]>([]);
  const [selectedDocumentTemplate, setSelectedDocumentTemplate] =
    useState<IDocumentTemplateOptions | null>(null);
  const [selectedDocumentTemplateData, setSelectedDocumentTemplateData] =
    useState<IDocumentTemplateData | null>(null);
  const [modalPresetValues, setModalPresetValues] = useState<any>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<string>("");
  const [lastSaved, setLastSaved] = useState<any>("");
  const { handleError, errorModal } = useErrorModal();
  const [editortype, setEditorType] = useState<any>("editor");
  const [documentTemplateType, setDocumentTemplateType] = useState<any>("all");
  const [textAreaValue, setTextareaValue] = useState<any>("");
  //eslint-disable-next-line
  const [editorValue, setEditorValue] = useState<any>("");
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();

  const { t } = useTranslation();
  const { showConfirmationModalAsync, showConfirmationModal } =
    useConfirmationModal();
  const { validation, reportSaveValidation } = useTemplateSyntaxValidation(
    editortype === "editor" ? editorValue : textAreaValue,
    selectedDocumentTemplate?.label ?? "",
    selectedDocumentTemplate !== null &&
      selectedDocumentTemplateData?.id === selectedDocumentTemplate.value
  );

  const currentContent = editortype === "editor" ? editorValue : textAreaValue;
  const dirty =
    isSaving ||
    parameterDraftDirty ||
    (!!selectedDocumentTemplateData &&
      (currentContent !== (selectedDocumentTemplateData.text ?? "") ||
        !isEqual(parameters, selectedDocumentTemplateData.parameters ?? [])));
  const onDirtyChange = props.onDirtyChange;
  useEffect(() => {
    onDirtyChange?.(editorId, dirty);
    return () => onDirtyChange?.(editorId, false);
  }, [dirty, editorId, onDirtyChange]);

  const confirmDiscard = () =>
    showConfirmationModalAsync(t("unsaved_formChanges_confirm"));

  const formatSyntaxError = (error: TemplateSyntaxError) =>
    [
      error.template,
      error.line && t("template_validation_line", { line: error.line }),
      error.message,
    ]
      .filter(Boolean)
      .join(": ");

  const showSyntaxErrorNotification = (errors: TemplateSyntaxError[]) => {
    setNotificationMessage(
      `${t("document_template_invalid")} ${formatSyntaxError(errors[0])}`
    );
    setNotificationVariant("error");
    setShowNotification(true);
  };

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  const mapDocumentTemplateToOption = (
    item: any
  ): IDocumentTemplateOptions => ({
    value: item.id,
    label: item.name,
    type: item.type,
  });

  const fetchDocumentTemplateById = useCallback(
    (id: string): Promise<IDocumentTemplateData> => {
      const sequence = ++requestSequence.current;
      return new Promise((resolve, reject) => {
        api.genericApiRequest({
          entity: "documentTemplate",
          method: "get",
          entityId: id,
          successHandler: (res: any) => {
            const template = res.data as IDocumentTemplateData;
            if (sequence !== requestSequence.current) {
              resolve(template);
              return;
            }
            setSelectedDocumentTemplateData(template);
            setParameters(template.parameters ?? []);
            setEditorValue(template.text);
            setTextareaValue(template.text);
            resolve(template);
          },
          failHandler: (error: any) => {
            if (sequence === requestSequence.current) handleError(error);
            reject(error);
          },
        });
      });
    },
    []
  );

  const loadDocumentTemplateOptions = useCallback(
    (inputValue: string): Promise<IDocumentTemplateOptions[]> => {
      if (inputValue.trim().length < 3) {
        return Promise.resolve([]);
      }

      const additionalUrlParameters: Record<string, string> = {
        name__icontains: inputValue,
      };

      if (documentTemplateType !== "all") {
        additionalUrlParameters.type = documentTemplateType;
      }

      return new Promise((resolve, reject) => {
        api.genericApiRequest({
          entity: "documentTemplate",
          method: "get",
          parametersToRender: {
            additionalUrlParameters,
            depth: "0",
          },
          successHandler: (res: any) => {
            const rawItems = Array.isArray(res?.data?.results)
              ? res.data.results
              : Array.isArray(res?.data)
              ? res.data
              : [];

            const mappedItems = rawItems
              .map(mapDocumentTemplateToOption)
              .filter((item: IDocumentTemplateOptions) => {
                if (documentTemplateType === "all") return true;
                return item.type === documentTemplateType;
              })
              .sort(
                (a: IDocumentTemplateOptions, b: IDocumentTemplateOptions) =>
                  a.label.localeCompare(b.label)
              );

            resolve(mappedItems);
          },
          failHandler: (error: any) => {
            handleError(error);
            reject(error);
          },
        });
      });
    },
    [documentTemplateType]
  );

  useEffect(
    () => () => {
      requestSequence.current += 1;
    },
    []
  );

  useEffect(() => {
    if (props.data !== undefined) {
      requestSequence.current += 1;
      setSelectedDocumentTemplate({
        value: props.data.id,
        label: props.data.name,
        type: props.data.type,
      });
      setSelectedDocumentTemplateData(props.data);
      setParameters(props.data.parameters ?? []);
      setEditorValue(props.data.text);
      setTextareaValue(props.data.text);
    }
  }, [props.data]);

  const handleChange = async (selectedOption: any) => {
    if (savePending.current || (dirty && !(await confirmDiscard()))) return;
    requestSequence.current += 1;
    setSelectedDocumentTemplateData(null);
    setParameters([]);
    setParameterDraftDirty(false);
    setEditorValue("");
    setTextareaValue("");
    if (!selectedOption) {
      setSelectedDocumentTemplate(null);
      setSelectedDocumentTemplateData(null);
      setEditorValue("");
      setTextareaValue("");
      return;
    }

    setSelectedDocumentTemplate(selectedOption);
    if (selectedOption.value !== undefined) {
      fetchDocumentTemplateById(String(selectedOption.value)).catch(() => null);
    }
  };

  const handleOnTypeChange = (event: any) => {
    setDocumentTemplateType(event.target.value);
  };

  const handleOnEditorTypeChange = async (event: any) => {
    const isConfirmed = await showConfirmationModalAsync(
      t("change_editor_type_confirm")
    );
    if (isConfirmed) {
      if (event.target.value === "textfield") {
        setTextareaValue(editorValue);
      } else {
        setEditorValue(textAreaValue);
      }
      setEditorType(event.target.value);
    }
  };

  const handleOnSave = (
    content: string,
    allowInvalidSyntax = false,
    acknowledgedErrors: TemplateSyntaxError[] = []
  ) => {
    if (
      !selectedDocumentTemplateData ||
      selectedDocumentTemplateData.id !== selectedDocumentTemplate?.value ||
      savePending.current
    )
      return;
    savePending.current = true;
    setIsSaving(true);
    const sequence = requestSequence.current;
    const result = {
      name: selectedDocumentTemplate?.label,
      type: selectedDocumentTemplate?.type,
      text: content,
      parameters,
      ...(allowInvalidSyntax && { allow_invalid_syntax: true }),
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "documentTemplate",
      method: "put",
      successHandler: (res: any) => {
        if (sequence !== requestSequence.current) return;
        savePending.current = false;
        setIsSaving(false);
        reportSaveValidation(acknowledgedErrors, content, result.name ?? "");
        setNotificationMessage(
          t(
            acknowledgedErrors.length
              ? "document_template_saved_with_syntax_errors"
              : "save_successful"
          )
        );
        setNotificationVariant(
          acknowledgedErrors.length ? "warning" : "success"
        );
        setShowNotification(true);
        const date = new Date(res.data.modified_on);
        setLastSaved(date.toLocaleString());
        setSelectedDocumentTemplate({
          value: res.data.id,
          label: res.data.name,
          type: res.data.type,
        });
        setSelectedDocumentTemplateData(res.data);
        setModalOpen(false);
      },
      failHandler: async (error: unknown) => {
        if (sequence !== requestSequence.current) return;
        savePending.current = false;
        setIsSaving(false);
        const errors = getTemplateSyntaxErrors(error);
        if (!errors.length) {
          handleError(error);
          return;
        }

        reportSaveValidation(errors, content, result.name ?? "");
        showSyntaxErrorNotification(errors);
        if (allowInvalidSyntax) return;

        const saveAnyway = await showConfirmationModalAsync(
          <div>
            <p>{t("document_template_invalid_save_message")}</p>
            <ul>
              {errors.map((syntaxError, index) => (
                <li
                  key={`${syntaxError.template}-${syntaxError.line}-${index}`}
                >
                  {formatSyntaxError(syntaxError)}
                </li>
              ))}
            </ul>
          </div>,
          {
            title: t("document_template_invalid_save_title"),
            confirmButtonText: t("document_template_save_anyway"),
          }
        );
        if (saveAnyway) handleOnSave(content, true, errors);
      },
      entityId: String(selectedDocumentTemplate?.value),
      submitData: result,
      config: config,
    });
  };

  const handleTextareaChange = (event: any) => {
    if (event) {
      setTextareaValue(event.target.value);
    }
  };

  const handleEditorChange = (value: any) => {
    setEditorValue(value);
  };

  const handleOnTextareaSave = () => handleOnSave(textAreaValue);

  const openCreateModal = () => {
    setModalMode("create");
    setModalPresetValues({
      status: "open",
    });
    setModalOpen(true);
  };

  const openEditModal = () => {
    setModalMode("edit");
    setModalPresetValues({
      name: selectedDocumentTemplate?.label,
      type: selectedDocumentTemplate?.type,
      text: selectedDocumentTemplateData?.text,
    });
    setModalOpen(true);
  };

  const openDuplicateModal = () => {
    setModalMode("duplicate");
    setModalPresetValues({ name: selectedDocumentTemplate?.label + "_1" });
    setModalOpen(true);
  };

  const handleAddDocumentTemplate = async (values: any) => {
    if (savePending.current || (dirty && !(await confirmDiscard()))) return;
    const prepared_values = {
      name: values.name,
      type: values.type,
      text: `New ${values.type}`,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "documentTemplate",
      method: "post",
      successHandler: (res: any) => {
        setSelectedDocumentTemplate({
          value: res.data.id,
          label: res.data.name,
          type: res.data.type,
        });
        requestSequence.current += 1;
        setSelectedDocumentTemplateData(res.data);
        setParameters(res.data.parameters ?? []);
        setParameterDraftDirty(false);
        setEditorValue(res.data.text);
        setTextareaValue(res.data.text);
        setModalOpen(false);
        setModalMode("");
      },
      failHandler: (error: any) => {
        handleError(error);
        setModalMode("");
      },
      submitData: prepared_values,
      config: config,
    });
  };

  const handleEditDocumentTemplateName = (values: any) => {
    if (savePending.current) return;
    const result = {
      name: values.name,
      type: modalPresetValues.type,
      text: modalPresetValues.text,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "documentTemplate",
      method: "put",
      successHandler: (res: any) => {
        setNotificationMessage(t("save_successful"));
        setNotificationVariant("success");
        setShowNotification(true);
        setSelectedDocumentTemplate({
          value: res.data.id,
          label: res.data.name,
          type: res.data.type,
        });
        setSelectedDocumentTemplateData(res.data);
        setModalOpen(false);
        setModalMode("");
      },
      failHandler: (error: any) => {
        handleError(error);
        setModalMode("");
      },
      entityId: String(selectedDocumentTemplate?.value),
      submitData: result,
      config: config,
    });
  };

  const copySelectedNameToClipboard = () => {
    if (selectedDocumentTemplate !== null) {
      navigator.clipboard.writeText(selectedDocumentTemplate?.label);
    }
  };

  const deleteDocumentTemplate = async (skipQuestion = false) => {
    if (savePending.current) return;
    if (selectedDocumentTemplate !== null) {
      if (!skipQuestion) {
        const isConfirmed = await showConfirmationModalAsync(
          t("delete_document_template_confirmation", {
            documentTemplateName: selectedDocumentTemplate?.label,
          })
        );
        if (!isConfirmed) {
          return;
        }
      }
      const fetchData = async () => {
        try {
          await api.genericApiRequest({
            entity: "documentTemplate",
            method: "delete",
            successHandler: () => {
              /*eslint-enable */
              setNotificationMessage(
                ` ${selectedDocumentTemplate?.label} t("delete_successful_all_lowercase")`
              );
              setNotificationVariant("success");
              setShowNotification(true);
              requestSequence.current += 1;
              setSelectedDocumentTemplate(null);
              setSelectedDocumentTemplateData(null);
              setParameters([]);
              setParameterDraftDirty(false);
              setEditorValue("");
              setTextareaValue("");
            },
            failHandler: (error: any) => {
              handleError(error);
            },
            entityId: String(selectedDocumentTemplate?.value),
          });
        } catch (error) {
          handleError(error);
        }
      };
      fetchData();
    } else
      showConfirmationModal(
        t("choose_document_template_before_delete_operation")
      );
  };

  const duplicateDocumentTemplate = (values: any) => {
    const result = {
      name: values.name,
      type: selectedDocumentTemplate?.type,
      text: selectedDocumentTemplateData?.text,
      parameters: selectedDocumentTemplateData?.parameters ?? [],
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "documentTemplate",
      method: "post",
      successHandler: (res: any) => {
        const data = {
          name: res.data.name,
          type: res.data.type,
          text: res.data.text,
          parameters: res.data.parameters,
          id: res.data.id,
        };
        props.onDuplicateEditor?.(data);
        setModalOpen(false);
        setModalMode("");
      },
      failHandler: (error: any) => {
        handleError(error);
        setModalMode("");
      },
      submitData: result,
      config: config,
    });
  };

  const tooltipName = () => {
    if (selectedDocumentTemplate !== null) {
      if (selectedDocumentTemplate?.type === "snippet") {
        return "Snippetname";
      }
      if (selectedDocumentTemplate?.type === "template") {
        return "Templatename";
      } else return "";
    }
  };

  const columnsForCreate: any = [
    {
      header: t("name"),
      accessorKey: "name",
      id: "name",
      enableForCreate: true,
      validate: true,
      rule: /^[a-z0-9_]*$/,
      helperText: `${t("use_lowercase")}, ${t("use_no_umlauts")}, ${t(
        "use_no_spaces"
      )}. ${t("use_underscore")}`,
      errorMessage: t("invalid_characters"),
      autoComplete: "off",
    },

    {
      header: t("type"),
      accessorKey: "type",
      id: "type",
      enableForCreate: true,
      selectOptions: [
        {
          value: "template",
          label: "template",
        },
        {
          value: "snippet",
          label: "snippet",
        },
      ],
    },
  ];

  const columnForEdit = [
    {
      header: t("name"),
      accessorKey: "name",
      id: "name",
      enableForCreate: true,
      validate: true,
      rule: /^[a-z0-9_]*$/,
      helperText: `${t("use_lowercase")}, ${t("use_no_umlauts")}, ${t(
        "use_no_spaces"
      )}. ${t("use_underscore")}`,
      errorMessage: t("invalid_characters"),
      autoComplete: "off",
    },
  ];

  return (
    <div className={styles.container}>
      <RadioButtons
        name={`type_${props.id}`}
        label={t("type")}
        defaultValue={documentTemplateType}
        choices={[
          { name: "all", label: t("all") },
          { name: "snippet", label: t("snippet") },
          { name: "template", label: t("template") },
        ]}
        onChange={(event: any) => handleOnTypeChange(event)}
      />
      <RadioButtons
        name={"editorType"}
        label={t("editortype")}
        defaultValue={editortype}
        choices={[
          { name: "textfield", label: t("textfield") },
          { name: "editor", label: t("editor") },
        ]}
        onChange={(event: any) => {
          handleOnEditorTypeChange(event);
        }}
      />
      <div className={styles.dropdownWrapper}>
        <div className={styles.dropDown__wrapper}>
          <FormFieldLabel
            label={t("templates_snippets")}
            htmlFor={`templates_${props.id}`}
            className={
              selectedDocumentTemplate !== null
                ? styles.dropDown__documentTemplatesSelected
                : styles.dropDown__documentTemplates
            }
          />
          <SubjectSearchSelect
            key={`templateEditor-search-${documentTemplateType}`}
            value={selectedDocumentTemplate}
            name={`templates_${props.id}`}
            loadOptions={loadDocumentTemplateOptions}
            cacheOptions={false}
            onChange={handleChange}
            isClearable={true}
          />
        </div>
        <Tooltip placement="top" title={t("refresh")}>
          <span>
            <GenericButton
              variant="icon"
              onClick={async () => {
                if (savePending.current || (dirty && !(await confirmDiscard())))
                  return;
                if (selectedDocumentTemplate?.value) {
                  fetchDocumentTemplateById(
                    String(selectedDocumentTemplate.value)
                  ).catch(() => null);
                }
              }}
              disabled={selectedDocumentTemplate ? false : true}
            >
              <RefreshIcon />
            </GenericButton>
          </span>
        </Tooltip>
        <Tooltip placement="top" title={t("edit")}>
          <span>
            <GenericButton
              variant="icon"
              onClick={() => openEditModal()}
              disabled={selectedDocumentTemplate ? false : true}
            >
              <EditIcon />
            </GenericButton>
          </span>
        </Tooltip>
        <Tooltip placement="top" title={t("copy", { title: tooltipName() })}>
          <span>
            <GenericButton
              variant="icon"
              onClick={copySelectedNameToClipboard}
              disabled={selectedDocumentTemplate ? false : true}
            >
              <ContentCopyIcon />
            </GenericButton>
          </span>
        </Tooltip>
        <Tooltip placement="top" title={t("delete")} color="error">
          <GenericButton
            variant="icon"
            onClick={() => deleteDocumentTemplate()}
          >
            <DeleteIcon />
          </GenericButton>
        </Tooltip>

        <Tooltip placement="top" title={t("create")}>
          <GenericButton variant="icon" onClick={() => openCreateModal()}>
            <AddIcon />
          </GenericButton>
        </Tooltip>
        <Tooltip
          placement="top"
          title={t("duplicate", { title: selectedDocumentTemplate?.type })}
        >
          <span>
            <GenericButton
              variant="icon"
              onClick={openDuplicateModal}
              disabled={selectedDocumentTemplate ? false : true}
            >
              <LibraryAddIcon />
            </GenericButton>
          </span>
        </Tooltip>
      </div>
      {dirty && selectedDocumentTemplateData && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mt: 2,
          }}
        >
          <Typography variant="body2">{t("unsaved_changes_hint")}</Typography>
          <GenericButton
            onClick={() => handleOnSave(currentContent)}
            disabled={isSaving || parameterDraftDirty}
          >
            {t("document_parameters.save_template")}
          </GenericButton>
        </Box>
      )}
      {selectedDocumentTemplate !== null && (
        <div className={styles.templateTools}>
          <TemplateVariables />
          {selectedDocumentTemplateData?.id ===
            selectedDocumentTemplate.value &&
            selectedDocumentTemplateData && (
              <TemplateParameterEditor
                value={parameters}
                onChange={setParameters}
                onDraftDirtyChange={setParameterDraftDirty}
              />
            )}
        </div>
      )}
      {selectedDocumentTemplate !== null && editortype === "editor" && (
        <div className={styles.tinyMceEditorWrapper}>
          <TinyMceEditor
            value={editorValue}
            handleOnSave={handleOnSave}
            lastSaved={lastSaved}
            onChange={handleEditorChange}
          />
        </div>
      )}
      {selectedDocumentTemplate !== null && editortype === "textfield" && (
        <>
          <TextArea
            rows={15}
            fieldClassName={styles.textArea__field}
            value={textAreaValue}
            onChange={handleTextareaChange}
            name="editorTextarea"
          />
          <div className={styles.buttonWrapper}>
            {lastSaved && typeof (lastSaved === "string") && (
              <div className={styles.lastSaved}>
                {t("last_saved")}: <span>{lastSaved}</span>
              </div>
            )}
            <GenericButton onClick={handleOnTextareaSave}>
              {t("save")}
            </GenericButton>
          </div>
        </>
      )}

      {validation && (
        <Alert
          severity={
            validation.status === "invalid"
              ? "error"
              : validation.status === "valid"
              ? "success"
              : validation.status === "unavailable"
              ? "warning"
              : "info"
          }
          role="status"
          sx={{ mt: 1 }}
        >
          {validation.status === "invalid"
            ? validation.errors.map(formatSyntaxError).join("; ")
            : t(`template_validation_${validation.status}`)}
        </Alert>
      )}

      <GenericCrudTableModal
        open={modalOpen}
        handleError={handleError}
        columns={modalMode === "create" ? columnsForCreate : columnForEdit}
        presetValues={modalPresetValues}
        onClose={() => {
          setModalOpen(false);
          setModalMode("");
        }}
        title={(function () {
          if (modalMode === "edit" || modalMode === "duplicate") {
            return t("edit_name");
          }
          return t("add_new_element");
        })()}
        submitButtonText={(function () {
          if (modalMode === "edit") {
            return t("save");
          }
          return t("add");
        })()}
        onSubmit={(function () {
          if (modalMode === "edit") {
            return handleEditDocumentTemplateName;
          } else if (modalMode === "duplicate") {
            return duplicateDocumentTemplate;
          } else return handleAddDocumentTemplate;
        })()}
      />
      <>
        {errorModal}

        <GenericNotification
          message={showNotification ? notificationMessage : null}
          variant={notificationVariant}
          handleCloseSnackbar={resetStateOnCloseNotification}
        />
      </>
    </div>
  );
}

//TODO: refactoring of API functions
//TODO: onchange on tiny? event needed for recognizing if any changes were made!
